import {getLatestTimestamp} from '@/ts/utils/pure-functions';
import type {TodayAssessmentDTO} from '@/ts/types/dto/today-dto';

interface ExploreTileStateOptions {
  /** The assessment tile's carousel, which carries when each was last answered. */
  assessments: readonly TodayAssessmentDTO[];
  /** When the content pair was chosen for this reader. */
  contentPinnedAt?: string | null;
  /**
   * When the reader last answered any assessment, which the payload carries at its root and knows
   * whole. Absent on a deployment that predates the field, and null for a reader who never has.
   */
  lastAssessmentAnsweredAt?: string | null;
  /**
   * The checklist's own answer to "has this reader ever answered an assessment", used only while a
   * backend that does not send the dates is still deployed somewhere.
   */
  assessmentAnswered?: boolean;
}

interface ExploreTileState {
  /** Nobody has answered anything yet: the tile's generic invitation, and the row it takes for it. */
  firstUse: boolean;
  /** The platform has learnt something since the content on the tile was chosen. */
  hasNewContent: boolean;
}

/**
 * Which face the Explore tile shows, from the two dates the Today payload carries (IPD-3256).
 *
 * "New content" is a comparison, not a claim about particular pieces: the pair on the tile was
 * chosen before the reader last answered, so Explore holds recommendations this tile does not
 * reflect. It stays true until the pair is chosen again, which can be up to three days — the state
 * is the normal one after answering, not a flash.
 */
export function resolveExploreTileState({
  assessments,
  contentPinnedAt,
  assessmentAnswered,
  lastAssessmentAnsweredAt,
}: ExploreTileStateOptions): ExploreTileState {
  const answeredAt = assessments
    .map((item) => item.lastAnsweredAt)
    .filter((value): value is string => Boolean(value));
  // The root date where the payload carries it, and the carousel's own where it does not yet.
  //
  // The two cannot disagree in the direction that matters: the carousel's dates are a subset of the
  // answers the root one is taken over, so it is the same instant or a later one. Where it is
  // absent — a deployment older than the field — the carousel is every answer still on offer, which
  // is what this read before and what it falls back to.
  //
  // Parsed rather than compared as text: the backend trims trailing zeros from a fractional second,
  // so the same instant arrives as both `…06:00:00Z` and `…06:00:00.5Z`, and `.` sorts below `Z`.
  // The repo's own helper already answers this, which is also why there is not a second one here.
  const lastAnsweredAt = lastAssessmentAnsweredAt
    ? new Date(lastAssessmentAnsweredAt).getTime()
    : getLatestTimestamp(answeredAt);
  const pinnedAt = contentPinnedAt ? new Date(contentPinnedAt).getTime() : 0;
  // Nothing will ever arrive for an account with no assessments, so the tile has nothing to
  // promise it, whichever question answers the rest.
  const hasAssessments = assessments.length > 0;

  return {
    // The checklist says it and the dates may veto it, in that order.
    //
    // Answering an assessment is one of the checklist's own three steps, so a reader it no longer
    // carries has taken them all and is past this by definition. The dates cannot stand in for it:
    // they come off the carousel the payload carries today, which is a recommendation list and
    // free to offer assessments this reader has never taken — read alone, that is indistinguishable
    // from a new account, and a reader who answered months ago would be handed the card and the
    // whole discover row with it. They can still overrule it, for the payload between an answer
    // landing and the checklist being recomputed.
    //
    // A backend that sends no dates leaves both of them empty, so this is the checklist alone
    // there, which is what it was before they existed.
    //
    // The root date is the one that can say this on its own, and it is the last word: it is taken
    // over every questionnaire the reader's packs offer rather than over what is on the carousel
    // today, so a date there means they have answered, whatever the rest of the payload has since
    // stopped carrying.
    firstUse: hasAssessments
      && assessmentAnswered === false
      && answeredAt.length === 0
      && !lastAssessmentAnsweredAt,
    hasNewContent: Boolean(lastAnsweredAt && pinnedAt && lastAnsweredAt > pinnedAt),
  };
}

